// ./src/utils/api/TicketOrderService.ts
//
// TicketOrder (a ticket purchase / sale) service — the box-office selling flow.
// Reusable engine on the server (see TicketOrderCore): reserve → confirm → issued
// tickets, concurrency-safe (never oversells). OpResult-native. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";
import type { AnswerAddressInput } from "@em/api";

import { ApiService } from "./ApiService";
import { EventTicket } from "./EventTicketService";

// =============================================================================
// Constants — mirror the server boundary
// =============================================================================

export const TICKET_ORDER_STATUS = {
  DRAFT: "draft",
  RESERVED: "reserved",
  PENDING_PAYMENT: "pending_payment",
  /**
   * #37 phase 2 — approval-gated ticketed sale: the buyer's card is AUTHORIZED and the tickets
   * are issued (that is what holds the seats), but no money has moved and every attendee sits in
   * `pending` until the host reviews them. Settles to `paid` on approval or `canceled` when
   * everyone is denied. Mirrors TICKET_ORDER_STATUS in the API's boundary/ticket.ts.
   */
  AWAITING_APPROVAL: "awaiting_approval",
  PAID: "paid",
  COMPLETED: "completed",
  CANCELED: "canceled",
  EXPIRED: "expired",
} as const;

/** Payment methods a box-office sale can be settled with. */
export const TICKET_PAYMENT_METHOD = {
  CASH: "cash",
  POS: "pos",
  CHEQUE: "cheque",
  COMP: "comp",
  OTHER: "other",
} as const;

export const TICKET_PAYMENT_METHOD_VALUES = Object.values(TICKET_PAYMENT_METHOD);

export const TICKET_PAYMENT_METHOD_LABELS: Record<string, string> = {
  [TICKET_PAYMENT_METHOD.CASH]: "Cash",
  [TICKET_PAYMENT_METHOD.POS]: "Card terminal",
  [TICKET_PAYMENT_METHOD.CHEQUE]: "Cheque",
  [TICKET_PAYMENT_METHOD.COMP]: "Complimentary",
  [TICKET_PAYMENT_METHOD.OTHER]: "Other",
};

// =============================================================================
// GraphQL
// =============================================================================

const TICKET_ORDER_FIELDS = `
  id
  eventId
  orderStatus
  saleChannel
  reservedUntil
  personId
  buyerName
  buyerEmail
  buyerPhone
  guestTracking
  currencyCode
  subtotalAmount
  totalAmount
  quantity
  paymentStatus
  placedAt
  createdAt
  tickets {
    id
    ticketCode
    ticketState
    ticketTypeId
    price
    currencyCode
    holderName
  }
`;

const RESERVE_MUTATION = gql`
  mutation TicketOrderReserve($params: TicketOrderReserveInput) {
    ticketOrderReserve(params: $params) {
      code
      data { ${TICKET_ORDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CONFIRM_MUTATION = gql`
  mutation TicketOrderConfirm($params: TicketOrderConfirmInput) {
    ticketOrderConfirm(params: $params) {
      code
      data { ${TICKET_ORDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CANCEL_MUTATION = gql`
  mutation TicketOrderCancel($params: TicketOrderCancelInput) {
    ticketOrderCancel(params: $params) {
      code
      data { id orderStatus }
      errors { name errors warnings }
    }
  }
`;

const AVAILABILITY_QUERY = gql`
  query TicketTypeAvailability($params: TicketAvailabilityInput) {
    ticketTypeAvailability(params: $params) {
      code
      data { ticketTypeId eventTimeSlotId name quantityTotal occupied available soldOut effectivePrice costs atThisPrice nextPrice }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface TicketOrder {
  id?: string;
  eventId?: string | null;
  orderStatus?: string | null;
  saleChannel?: string | null;
  reservedUntil?: string | null;
  personId?: string | null;
  buyerName?: string | null;
  buyerEmail?: string | null;
  buyerPhone?: string | null;
  guestTracking?: string | null;
  currencyCode?: string | null;
  subtotalAmount?: string | null;
  totalAmount?: string | null;
  quantity?: number | null;
  paymentStatus?: string | null;
  placedAt?: string | null;
  createdAt?: string;
  tickets?: EventTicket[] | null;
}

export interface TicketAvailability {
  ticketTypeId?: string;
  /** ROADMAP A4: the date these counts are for, when one was asked for. */
  eventTimeSlotId?: string | null;
  name?: string | null;
  quantityTotal?: number | null;
  occupied?: number | null;
  available?: number | null;
  soldOut?: boolean | null;
  /** #282 — what one costs now, and what 1, 2, … cost together when a count changes the price (`ticketsCost` reads them). */
  effectivePrice?: number | null;
  costs?: number[] | null;
  /** How many more sell at this price, and the price after. Null when sales do not change it. */
  atThisPrice?: number | null;
  nextPrice?: number | null;
}

export interface TicketOrderItemInput {
  ticketTypeId: string;
  quantity: number;
  /** ROADMAP A4: the date this line is for; omitted = the order-level `eventTimeSlotId`. */
  eventTimeSlotId?: string | null;
}

export interface TicketOrderReserveInput {
  eventId: string;
  eventTimeSlotId?: string | null;
  saleChannel?: string | null;
  reservationMinutes?: number | null;
  items: TicketOrderItemInput[];
  /** The buyer's membership number — unlocks members-only tickets and member prices (checked on the server). */
  membershipNumber?: string | null;
}

export interface TicketOrderBuyerInput {
  personId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  /** A2e #87 — optional at the box office; when given it needs a street, city and country. */
  address?: AnswerAddressInput | null;
}

export interface TicketOrderGuestInput {
  ticketId: string;
  personId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface TicketOrderConfirmInput {
  orderId: string;
  method?: string | null;
  processorPaymentId?: string | null;
  note?: string | null;
  buyer: TicketOrderBuyerInput;
  /** When true (names mode): the buyer is also an attendee (fills the first ticket). */
  buyerIsAttendee?: boolean;
  guests?: TicketOrderGuestInput[];
}

// =============================================================================
// Service
// =============================================================================

export class TicketOrderService {
  static async reserve(params: TicketOrderReserveInput): Promise<OpResult> {
    return ApiService.executeMutation(RESERVE_MUTATION, { params });
  }

  static async confirm(params: TicketOrderConfirmInput): Promise<OpResult> {
    return ApiService.executeMutation(CONFIRM_MUTATION, { params });
  }

  static async cancel(orderId: string): Promise<OpResult> {
    return ApiService.executeMutation(CANCEL_MUTATION, { params: { orderId } });
  }

  /**
   * ROADMAP A4: `eventTimeSlotId` counts one date of a recurring event, where stock is per date. #282: `membershipNumber`
   * prices the list for that member, as the reserve will; one that is not valid here is refused on its own field.
   */
  static async availability(params: { eventId?: string; ticketTypeIds?: string[]; eventTimeSlotId?: string | null; membershipNumber?: string | null }): Promise<OpResult> {
    return ApiService.executeQuery(AVAILABILITY_QUERY, { params });
  }
}
